'use client';

import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { UnifiedModal } from '@/app_components/ui/UnifiedModal';
import {
  deleteFortuneReleaseCookie,
  getFortuneReleaseCookie,
  setFortuneReleaseCookie,
} from '@/app_lib/utils/fortuneReleaseCookie';

interface FortuneReleaseModalRef {
  openModal: () => void;
}

interface FortuneReleaseModalProps {
  trigger?: React.ReactNode;
}

const FortuneReleaseModal = forwardRef<FortuneReleaseModalRef, FortuneReleaseModalProps>(({ trigger }, ref) => {
  const [currentValue, setCurrentValue] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('release-candidate');
  const [isOpen, setIsOpen] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);

  useImperativeHandle(ref, () => ({
    openModal: () => setIsOpen(true),
  }));

  useEffect(() => {
    // Get current cookie value on component mount
    const value = getFortuneReleaseCookie();
    setCurrentValue(value);
  }, []);

  const checkAuthorization = useCallback(async () => {
    setIsCheckingAuth(true);
    try {
      const response = await fetch('/api/fortune-release/authorize/', {
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      });
      const data = await response.json();
      setIsAuthorized(data.authorized === true);
    } catch {
      setIsAuthorized(false);
    } finally {
      setIsCheckingAuth(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && isAuthorized === null) {
      checkAuthorization();
    }
  }, [isOpen, checkAuthorization, isAuthorized]);

  const handleSetCookie = () => {
    if (inputValue.trim()) {
      setFortuneReleaseCookie(inputValue.trim());
      setCurrentValue(inputValue.trim());
      // Refresh the page to get content from the new backend
      window.location.reload();
    }
  };

  const handleDeleteCookie = () => {
    deleteFortuneReleaseCookie();
    setCurrentValue(null);
    // Refresh the page to return to production backend
    window.location.reload();
  };

  const getModalContent = () => {
    if (isCheckingAuth) {
      return (
        <div className='text-center py-8'>
          <div className='text-sm text-gray-600'>Checking authorization...</div>
        </div>
      );
    }

    if (isAuthorized === false) {
      return (
        <div className='text-center py-8'>
          <div className='text-2xl font-bold text-red-600'>NOT AUTHORIZED</div>
          <p className='text-sm text-gray-600 mt-2'>You do not have permission to access this feature.</p>
        </div>
      );
    }

    if (isAuthorized === true) {
      return (
        <>
          <div className='space-y-2'>
            <div className='text-sm font-medium text-gray-700'>Current Fortune Cookie Value:</div>
            <div className='text-sm text-gray-600 bg-gray-50 p-2 rounded border min-h-[40px] flex items-center'>
              {currentValue ? (
                <code className='font-mono'>{currentValue}</code>
              ) : (
                <span className='italic'>Not set (using production backend)</span>
              )}
            </div>
          </div>

          <div className='space-y-2'>
            <label
              className='text-sm font-medium text-gray-700'
              htmlFor='cookie-value'
            >
              Set Fortune Cookie Value:
            </label>
            <input
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              id='cookie-value'
              onChange={(e) => setInputValue(e.target.value)}
              placeholder='Enter backend route value'
              type='text'
              value={inputValue}
            />
            <p className='text-xs text-gray-500'>This will route traffic to the specified backend service</p>
          </div>
          <p className='text-xs text-gray-500'>Page will refresh automatically after setting or deleting the cookie</p>
        </>
      );
    }

    return null;
  };

  const modalContent = <div className='p-2 space-y-4'>{getModalContent()}</div>;

  return (
    <>
      {trigger && trigger}
      <div
        className='contents'
        data-fortune-release-modal
      >
        <UnifiedModal
          className='fortune-release-modal'
          firstButton={
            isAuthorized
              ? { dataCy: 'setCookieBtn', onClick: handleSetCookie, title: 'Set Cookie & Refresh' }
              : undefined
          }
          isOpen={isOpen}
          onOpenChange={(open) => setIsOpen(open)}
          secondButton={
            isAuthorized
              ? { dataCy: 'deleteCookieBtn', onClick: handleDeleteCookie, title: 'Delete Cookie & Refresh' }
              : undefined
          }
          title='Fortune Cookie Release Manager'
        >
          {modalContent}
        </UnifiedModal>
      </div>
    </>
  );
});

FortuneReleaseModal.displayName = 'FortuneReleaseModal';

// biome-ignore lint: Component must export its ref type interface alongside
export { FortuneReleaseModal, type FortuneReleaseModalRef };
